import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { db, storage, functions } from './firebase'; // Assuming firebase.js is in src
import { collection, query, where, orderBy, limit, onSnapshot, getDoc, startAfter, endBefore, getDocs, deleteDoc, doc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from 'firebase/functions';
import './HistoryPage.css'; // Create this CSS file for specific styling
import { usePostHog } from 'posthog-js/react';

// Helper to get activity icon using Tabler icons from CDN
const ActivityIcon = ({ type, size = 24 }) => {
    let iconClass = 'ti ti-activity'; // default icon

    switch (type?.toLowerCase()) {
        case 'run':
            iconClass = 'ti ti-run';
            break;
        case 'ride':
        case 'virtualride':
        case 'ebikeride':
            iconClass = 'ti ti-bike';
            break;
        case 'walk':
            iconClass = 'ti ti-walk';
            break;
        case 'swim':
            iconClass = 'ti ti-swimming';
            break;
        case 'hike':
            iconClass = 'ti ti-trekking';
            break;
        case 'workout':
            iconClass = 'ti ti-stretching';
            break;
        default:
            iconClass = 'ti ti-activity';
    }

    return <i className={iconClass} style={{ fontSize: `${size}px` }}></i>;
};

// Delete confirmation modal component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, activityName, isDeleting }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{t('history.deleteMapTitle')}</h3>
                    <button className="modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <p>{t('history.deleteConfirm', { name: activityName || t('history.thisMap') })}</p>
                    <p className="delete-warning">{t('settings.cannotUndo')}</p>
                </div>
                <div className="modal-actions">
                    <button className="button-secondary" onClick={onClose} disabled={isDeleting}>
                        {t('common.cancel')}
                    </button>
                    <button className="button-danger" onClick={onConfirm} disabled={isDeleting}>
                        {isDeleting ? t('common.deleting') : t('history.deleteCta')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper to format duration from seconds to HH:MM:SS or MM:SS
const formatDuration = (totalSeconds) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const pad = (num) => String(num).padStart(2, '0');

    if (hours > 0) {
        return `${hours}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(minutes)}:${pad(seconds)}`;
};

function HistoryPage({ user }) { // Receives user as a prop
    const { t } = useTranslation();
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const posthog = usePostHog();

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        requestId: null,
        activityName: null,
        isDeleting: false
    });

        // Helper function to check if map has already earned a credit
    const hasEarnedCredit = (request) => {
        // Must have Strava ID and watermark detected (credit earned)
        return request.StravaId && request.hasWatermark;
    };

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;
    const [lastVisible, setLastVisible] = useState(null);
    const [firstVisible, setFirstVisible] = useState(null);
    const [isLastPage, setIsLastPage] = useState(false);

    // Sorting state - Set Date as default
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    const [pageCursors, setPageCursors] = useState([null]); // null for the first page

    const fetchRequests = async (pageIndex = currentPage - 1, isSortChange = false) => {
        if (!user) return;
        setIsLoading(true);
        setError(null);

        try {
            let q = collection(db, "GenerationRequests");
            let conditions = [where("userId", "==", user.uid)];

            // Sorting
            if (sortBy === 'StravaActivityName') {
                conditions.push(where("stravaStats.activityName", "!=", null));
                conditions.push(orderBy("stravaStats.activityName", sortOrder));
                conditions.push(orderBy("createdAt", "desc"));
            } else {
                conditions.push(orderBy("createdAt", sortOrder));
            }

            // Pagination
            if (pageCursors[pageIndex]) {
                conditions.push(startAfter(pageCursors[pageIndex]));
            }
            conditions.push(limit(itemsPerPage));

            const finalQuery = query(q, ...conditions);
            const documentSnapshots = await getDocs(finalQuery);

            const requestsWithThumbnails = await Promise.all(documentSnapshots.docs
                .filter(doc => doc.data().status !== 'deleted') // Filter out deleted maps
                .map(async (doc) => {
                    const data = doc.data();
                    let thumbnailUrl = null;
                    const imagePathToTry = data.generatedImagePath || data.originalImagePath;
                    if (imagePathToTry) {
                        try {
                            thumbnailUrl = await getDownloadURL(ref(storage, imagePathToTry));
                        } catch (e) {
                            console.warn(`Failed to get thumbnail URL for ${imagePathToTry}:`, e.message);
                        }
                    }
                    return { id: doc.id, ...data, thumbnailUrl };
                }));

            setRequests(requestsWithThumbnails);

            // Update cursors for next/prev navigation
            if (documentSnapshots.docs.length > 0) {
                const newCursors = [...pageCursors];
                newCursors[pageIndex + 1] = documentSnapshots.docs[documentSnapshots.docs.length - 1];
                setPageCursors(newCursors);
            }

            setIsLastPage(documentSnapshots.docs.length < itemsPerPage);

        } catch (err) {
            console.error("Error fetching history:", err);
            setError(t('history.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!user) {
            setRequests([]);
            setIsLoading(false);
            return;
        }
        fetchRequests(currentPage - 1);
        // eslint-disable-next-line
    }, [user, currentPage, sortBy, sortOrder]);

    const handleRequestClick = (requestId) => {
        posthog?.capture('history_map_opened', {
            request_id: requestId,
            source: 'history_page'
        });
        navigate(`/map/${requestId}`);
    };

    const handleDeleteClick = (e, requestId, activityName) => {
        e.stopPropagation(); // Prevent navigation
        setDeleteModal({
            isOpen: true,
            requestId,
            activityName,
            isDeleting: false
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.requestId) return;

        setDeleteModal(prev => ({ ...prev, isDeleting: true }));

        try {
            // Call the deleteMap cloud function
            const deleteMapFunc = httpsCallable(functions, 'deleteMap');
            const result = await deleteMapFunc({ 
                requestId: deleteModal.requestId,
                reason: "User deleted from history page"
            });

            // Remove from local state
            setRequests(prev => prev.filter(req => req.id !== deleteModal.requestId));

            // Track deletion
            posthog?.capture('history_map_deleted', {
                request_id: deleteModal.requestId,
                activity_name: deleteModal.activityName
            });

            // Close modal
            setDeleteModal({
                isOpen: false,
                requestId: null,
                activityName: null,
                isDeleting: false
            });

        } catch (err) {
            console.error("Error deleting map:", err);
            let errorMessage = "Failed to delete the map. Please try again.";
            if (err.code === 'permission-denied') {
                errorMessage = "You don't have permission to delete this map.";
            } else if (err.code === 'not-found') {
                errorMessage = "Map not found.";
            }
            setError(errorMessage);
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModal({
            isOpen: false,
            requestId: null,
            activityName: null,
            isDeleting: false
        });
    };

    const handleSortChange = (newSortBy) => {
        posthog?.capture('history_sort_changed', {
            sort_by: newSortBy,
            previous_sort: sortBy
        });

        if (newSortBy === sortBy) {
            setSortOrder(currentOrder => currentOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(newSortBy);
            setSortOrder(newSortBy === 'StravaActivityName' ? 'asc' : 'desc');
        }
        setCurrentPage(1);
        setPageCursors([null]); // Reset cursors
    };

    const goToNextPage = () => {
        if (!isLastPage) {
            posthog?.capture('history_pagination', {
                action: 'next_page',
                page: currentPage + 1
            });
            setCurrentPage(prev => prev + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            posthog?.capture('history_pagination', {
                action: 'previous_page',
                page: currentPage - 1
            });
            setCurrentPage(prev => prev - 1);
        }
    };

    if (!user) {
        return (
            <div className="container history-page">
                <p style={{ textAlign: 'center' }}>Please <Link to="/">sign in</Link> to view your maps.</p>
            </div>
        );
    }

    return (
        <div className="container history-page">
            <h1>{t('history.title', 'Your Mythic Maps')}</h1>
            <p className="history-subtitle">{t('history.subtitle', 'Explore and share your most mythic adventures')}</p>
            <div className="history-divider"></div>

            <div className="history-controls">
                                <div className="sort-options">
                    <button
                        onClick={() => handleSortChange('createdAt')}
                        className={sortBy === 'createdAt' ? 'active' : ''}
                    >
                                        {t('landing.sortByDate')}
                    </button>
                    <button
                        onClick={() => handleSortChange('StravaActivityName')}
                        className={sortBy === 'StravaActivityName' ? 'active' : ''}
                    >
                                        {t('landing.sortByName')}
                    </button>
                </div>
            </div>

            {isLoading && <p style={{ textAlign: 'center' }}>Loading your maps...</p>}
            {error && <p className="error text-center">{error}</p>}
            {!isLoading && requests.length === 0 && !error && (
                <p style={{ textAlign: 'center' }}>You have no maps yet. <Link to="/">Create one!</Link></p>
            )}

            {!isLoading && requests.length > 0 && (
                <ul className="history-requests-list">
                    {requests.map(req => (
                        <li key={req.id} onClick={() => handleRequestClick(req.id)} title="View and share this map">
                            <button 
                                className="delete-button"
                                onClick={(e) => handleDeleteClick(e, req.id, req.stravaStats?.activityName)}
                                title="Delete this map"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M13.3333 4.99984V4.33317C13.3333 3.39975 13.3333 2.93304 13.1517 2.57652C12.9919 2.26292 12.7369 2.00795 12.4233 1.84816C12.0668 1.6665 11.6001 1.6665 10.6667 1.6665H9.33333C8.39991 1.6665 7.9332 1.6665 7.57668 1.84816C7.26308 2.00795 7.00811 2.26292 6.84832 2.57652C6.66667 2.93304 6.66667 3.39975 6.66667 4.33317V4.99984M8.33333 9.58317V13.7498M11.6667 9.58317V13.7498M2.5 4.99984H17.5M15.8333 4.99984V14.3332C15.8333 15.7333 15.8333 16.4334 15.5608 16.9681C15.3212 17.4386 14.9387 17.821 14.4683 18.0607C13.9335 18.3332 13.2335 18.3332 11.8333 18.3332H8.16667C6.76654 18.3332 6.06647 18.3332 5.53169 18.0607C5.06129 17.821 4.67883 17.4386 4.43915 16.9681C4.16667 16.4334 4.16667 15.7333 4.16667 14.3332V4.99984" stroke="#FFFFFF" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <div className="history-thumbnail">
                                {req.thumbnailUrl ?
                                    <img src={req.thumbnailUrl} alt={`${req.stravaStats?.activityName || 'Map'} thumbnail`} /> :
                                    <div className="thumbnail-placeholder">No Preview</div>}
                                {hasEarnedCredit(req) && (
                                    <div className="credit-badge" title="You earned +1 credit for sharing this map on Strava">
                                        +1
                                    </div>
                                )}
                            </div>
                            <div className="history-details">
                                <div className="activity-header">
                                    <div className="activity-title-section">
                                        <ActivityIcon type={req.stravaStats?.activityType} size={20} />
                                        <h3 className="activity-name">{req.stravaStats?.activityName || 'Untitled Map'}</h3>
                                    </div>
                                    <p className="activity-date">
                                        {req.stravaStats?.activityDate ? new Date(req.stravaStats.activityDate.seconds * 1000).toLocaleDateString() : (req.createdAt?.toDate().toLocaleDateString() || 'N/A')}
                                    </p>
                                </div>
                                <div className="activity-metrics">
                                    <div>
                                        <span>{t('history.distance', 'Distance')}</span>
                                        <strong>{req.stravaStats?.distance ? (req.stravaStats.distance / 1000).toFixed(1) : 'N/A'} km</strong>
                                    </div>
                                    <div>
                                        <span>{t('history.elevation', 'Elevation')}</span>
                                        <strong>{req.stravaStats?.elevationGain?.toFixed(0) || 'N/A'} m</strong>
                                    </div>
                                    <div>
                                        <span>{t('history.time', 'Time')}</span>
                                        <strong>{formatDuration(req.stravaStats?.movingTime) || 'N/A'}</strong>
                                    </div>
                                    {req.stravaStats?.activityType?.toLowerCase().includes('run') && req.stravaStats?.averageSpeed ? (
                                        <div>
                                            <span>{t('history.pace', 'Pace')}</span>
                                            <strong>{(() => {
                                                const paceSecondsPerKm = (1 / req.stravaStats.averageSpeed) * 1000;
                                                const paceMinutes = Math.floor(paceSecondsPerKm / 60);
                                                const paceSeconds = Math.floor(paceSecondsPerKm % 60);
                                                return `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')} /km`;
                                            })()}</strong>
                                        </div>
                                    ) : req.stravaStats?.averageWatts ? (
                                        <div>
                                            <span>{t('history.power', 'Power')}</span>
                                            <strong>{req.stravaStats.averageWatts.toFixed(0)} W</strong>
                                        </div>
                                    ) : null}
                                </div>
                                {(req.prompt || req.customPromptText || req.promptTemplateId) && (
                                    <p className="prompt-snippet">
                                        <em>
                                            {req.prompt ?
                                                (req.prompt.substring(0, 50) + (req.prompt.length > 50 ? '...' : '')) :
                                                (req.customPromptText ?
                                                    `Template: ${req.promptTemplateId} + ${req.customPromptText.substring(0, 30)}${req.customPromptText.length > 30 ? '...' : ''}` :
                                                    (req.promptTemplateId ? `Template: ${req.promptTemplateId}` : 'N/A')
                                                )
                                            }
                                        </em>
                                    </p>
                                )}
                            </div>
                            <button className="view-map-button" onClick={(e) => { e.stopPropagation(); handleRequestClick(req.id); }}>See & Share</button>
                        </li>
                    ))}
                </ul>
            )}

            {!isLoading && requests.length > 0 && (
                <div className="pagination-controls">
                    <button onClick={goToPreviousPage} disabled={currentPage === 1 || isLoading}>
                        &larr; {t('landing.previous')}
                    </button>
                    <span>{t('landing.pageOf', { current: currentPage, total: '…' })}</span>
                    <button onClick={goToNextPage} disabled={isLastPage || isLoading}>
                        {t('landing.next')} &rarr;
                    </button>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                activityName={deleteModal.activityName}
                isDeleting={deleteModal.isDeleting}
            />
        </div>
    );
}

export default HistoryPage; 